import { BlockInstanceType } from '../../blocks/BlockInterface';
import { PropertiesInterface, PropertyType } from '../Property';
import { BlockRegistry } from './blockRegistry';
import {
  BlockPropertyNameGetter,
  BlockPropertyTypeFilterPredicate,
} from './types';

export type NamedPropertyType = {
  name: string;
  propertyType: PropertyType;
};

export class BlockInstancePropertiesExtractor {
  constructor(
    protected rootBlockInstance: BlockInstanceType,
    protected blockRegistry: BlockRegistry,
    protected blockPropertyNameGetter: BlockPropertyNameGetter,
    protected propertyTypeFilterPredicate: BlockPropertyTypeFilterPredicate
  ) {}

  extractPropertiesInterface(): PropertiesInterface {
    const blockProperties = this.getPropertyTypesForBlockInstanceAndChildren(
      this.rootBlockInstance
    );

    const properties: PropertiesInterface = {};

    blockProperties.forEach((blockProperty: NamedPropertyType) => {
      if (blockProperty.name in properties) {
        properties[blockProperty.name] = this.mergeProperties(
          blockProperty.name,
          blockProperty.propertyType,
          properties[blockProperty.name]
        );
      } else {
        properties[blockProperty.name] = blockProperty.propertyType;
      }
    });

    return properties;
  }

  mergeProperties(
    propertyName: string,
    _propertyTypeA: PropertyType,
    _propertyTypeB: PropertyType
  ): PropertyType {
    throw new Error(
      `There is more than one property with name "${propertyName}" defined. Please make sure that property names are unique.`
    );
  }

  getPropertyTypesForBlockInstanceAndChildren(
    block: BlockInstanceType,
    parents: Array<BlockInstanceType> = []
  ): Array<NamedPropertyType> {
    const blockPropertyType = this.getPropertyTypesForBlockInstance(
      block,
      parents
    );

    if (!block.properties?.children) {
      return blockPropertyType;
    }

    return [
      ...blockPropertyType,
      ...block.properties.children.flatMap((childBlock: any) =>
        this.getPropertyTypesForBlockInstanceAndChildren(childBlock, [
          block,
          ...parents,
        ])
      ),
    ];
  }

  getPropertyTypesForBlockInstance(
    block: BlockInstanceType,
    parents: Array<BlockInstanceType>
  ): Array<NamedPropertyType> {
    if (!block.properties) {
      return [];
    }

    return Object.keys(block.properties)
      .filter(blockPropertyName =>
        this.propertyTypeFilterPredicate.shouldAllowBlockPropertyType(
          block,
          blockPropertyName
        )
      )
      .map(filteredBlockPropertyName => ({
        name: this.blockPropertyNameGetter.get(
          block,
          filteredBlockPropertyName,
          parents
        ),
        propertyType: this.blockRegistry.getBlockTypePropertyTypeCopy(
          block.blockId!,
          filteredBlockPropertyName
        ),
      }));
  }
}
